import { Point, Rectangle } from '@awayjs/core';
import { ContextGLDrawMode } from '../base/ContextGLDrawMode';
import { ContextGLCompareMode } from '../base/ContextGLCompareMode';
import { ContextGLVertexBufferFormat } from '../base/ContextGLVertexBufferFormat';
import { Image2D } from '../image/Image2D';
import { ImageSampler } from '../image/ImageSampler';
import { ContextGLClearMask } from '../base/ContextGLClearMask';
import { ContextGLTriangleFace } from '../base/ContextGLTriangleFace';
import { ContextGLBlendFactor } from '../base/ContextGLBlendFactor';
import { DisplacementFilter, BlurFilter, BevelFilter, ThresholdFilter, CopyPixelFilter } from '../filters';
var tmpRect0 = new Rectangle();
var FilterManager = /** @class */ (function () {
    function FilterManager(_stage) {
        var _a;
        this._stage = _stage;
        this._texturePool = {};
        this._filterCache = {};
        this._filterConstructors = (_a = {},
            _a[BevelFilter.filterName] = BevelFilter,
            _a[BlurFilter.filterName] = BlurFilter,
            _a[DisplacementFilter.filterName] = DisplacementFilter,
            _a);
        FilterManager._instance = this;
    }
    Object.defineProperty(FilterManager, "instance", {
        get: function () {
            return this._instance;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FilterManager.prototype, "context", {
        get: function () {
            return this._stage.context;
        },
        enumerable: false,
        configurable: true
    });
    FilterManager.prototype.popTemp = function (width, height, msaa) {
        if (msaa === void 0) { msaa = false; }
        width = Math.max(FilterManager.MIN_TMP_TEXTURE, 2 << Math.log2(width - 1));
        height = Math.max(FilterManager.MIN_TMP_TEXTURE, 2 << Math.log2(height - 1));
        if (width > FilterManager.MAX_TMP_TEXTURE || height > FilterManager.MAX_TMP_TEXTURE) {
            // eslint-disable-next-line max-len
            console.warn("[Filter manager] Temporary texture size " + width + "x" + height + " is bigger that limit, clamp to " + FilterManager.MAX_TMP_TEXTURE);
            width = Math.min(width, FilterManager.MAX_TMP_TEXTURE);
            height = Math.min(height, FilterManager.MAX_TMP_TEXTURE);
        }
        msaa = msaa && this.context.glVersion === 2;
        var key = width + "_" + height + "_" + ~~msaa;
        var image;
        if (!this._texturePool[key]) {
            this._texturePool[key] = [];
        }
        else {
            image = this._texturePool[key].pop();
        }
        if (!image) {
            // TODO
            // we have problems with cyclic imports
            // and can't create TmpImage2D class, use a regular Image2D with mixin
            image = (new Image2D(width, height, true));
            image.poolKey = key;
            image.antialiasQuality = msaa ? 8 : 0;
        }
        return image;
    };
    FilterManager.prototype.pushTemp = function (image) {
        var im = image;
        if (im && im.poolKey) {
            this._texturePool[im.poolKey].push(im);
        }
    };
    FilterManager.prototype._initFilterElements = function () {
        if (this._filterVertexBuffer) {
            return;
        }
        this._filterVertexBuffer = this.context.createVertexBuffer(4, 8);
        this._filterVertexBuffer.uploadFromArray(new Float32Array([
            0, 0,
            1, 0,
            1, 1,
            0, 1
        ]), 0, 4);
        this._filterIndexBuffer = this.context.createIndexBuffer(6);
        this._filterIndexBuffer.uploadFromArray(new Uint16Array([
            2, 1, 0,
            3, 2, 0
        ]), 0, 6);
        this._filterSampler = new ImageSampler(false, true, false);
    };
    FilterManager.prototype._bindFilterElements = function () {
        var ctx = this.context;
        if (!this._filterVAO) {
            // if there are not VAO, but support - create it and bind for attach attributes
            var vao = ctx.hasVao ? ctx.createVao() : null;
            vao && vao.bind();
            ctx.setVertexBufferAt(0, this._filterVertexBuffer, 0, ContextGLVertexBufferFormat.FLOAT_2);
            // we can bound index buffer directly, but to track index rebound state should attach it to vao
            vao
                ? vao.attachIndexBuffer(this._filterIndexBuffer)
                : ctx.bindIndexBuffer(this._filterIndexBuffer);
            this._filterVAO = vao;
        }
        else {
            // already init, bind it
            this._filterVAO.bind();
        }
    };
    FilterManager.prototype._unbindFilterElemens = function () {
        // to be sure that VAO wasn't corrupted by next shader
        this._filterVAO && this._filterVAO.unbind(true);
    };
    FilterManager.prototype.applyFilter = function (source, target, sourceRect, destRectOrPoint, filterName, options) {
        if (!this._filterCache[filterName] && !this._filterConstructors[filterName]) {
            console.warn('[FilterManager] Filter not implemented:', filterName);
            return false;
        }
        var filter = this._filterCache[filterName];
        if (filter) {
            filter.applyProps(options);
        }
        else {
            filter = this._filterCache[filterName] = new this._filterConstructors[filterName](options);
        }
        this.context.setBlendFactors(ContextGLBlendFactor.ONE, ContextGLBlendFactor.ONE_MINUS_SOURCE_ALPHA);
        this.renderFilter(source, target, sourceRect || source.rect, destRectOrPoint || source.rect, filter);
        return true;
    };
    FilterManager.prototype.renderFilter = function (source, target, sourceRect, targetRect, filter) {
        this._initFilterElements();
        var outRect = tmpRect0;
        if (targetRect instanceof Point) {
            outRect.setTo(targetRect.x, targetRect.y, sourceRect.width, sourceRect.height);
        }
        else {
            outRect.copyFrom(targetRect);
        }
        var renderToSelf = source === target;
        // tmp texture, avoid framebuffer loop
        var output = target;
        if (renderToSelf) {
            // we render to tmp, not require use offset
            output = this.popTemp(outRect.width, outRect.height);
            outRect.x = 0;
            outRect.y = 0;
        }
        filter.setRenderState(source, output, sourceRect, outRect, this);
        //render
        var indexBuffer = this._filterIndexBuffer;
        var tasks = filter.tasks;
        if (filter.requireBlend) {
            // context will enable blend when it was changed
            this.context.setBlendFactors(filter.blendSrc, filter.blendDst);
        }
        else {
            this.context.setBlendState(false);
        }
        this.context.setCulling(ContextGLTriangleFace.NONE);
        // vao binds require shader, other shaders MUST use same locations
        this.context.setProgram(tasks[0].getProgram(this._stage));
        this._bindFilterElements();
        for (var _i = 0, tasks_1 = tasks; _i < tasks_1.length; _i++) {
            var task = tasks_1[_i];
            task.preActivate(this._stage);
            this._stage.setRenderTarget(task.target, false);
            this._stage.setScissor(task.clipRect);
            // because we use TMP image, need clear it
            // but this is needed only when a blend composer is required, when a copy filter used
            if ((renderToSelf || task.needClear) && filter.requireBlend) {
                this._stage.clear(0, 0, 0, 0, 0, 0, ContextGLClearMask.ALL);
            }
            this.context.setProgram(task.getProgram(this._stage));
            this.context.setDepthTest(false, ContextGLCompareMode.LESS_EQUAL);
            if (!task.activateInternaly) {
                task.source
                    .getAbstraction(this._stage)
                    .activate(task.sourceSamplerIndex, this._filterSampler);
            }
            task.activate(this._stage, null, null);
            this.context.drawIndices(ContextGLDrawMode.TRIANGLES, indexBuffer, 0, 6);
            task.deactivate(this._stage);
        }
        if (renderToSelf) {
            // copy output to target texture
            this.copyPixels(output, target, outRect, targetRect, false);
            this.pushTemp(output);
        }
        filter.clear(this);
        this._stage.setScissor(null);
        this._unbindFilterElemens();
    };
    FilterManager.prototype.copyPixels = function (source, target, rect, destPoint, mergeAlpha, blend) {
        if (mergeAlpha === void 0) { mergeAlpha = false; }
        if (blend === void 0) { blend = ''; }
        //early out for values that won't produce any visual update
        if (destPoint.x < -rect.width
            || destPoint.x > target.width
            || destPoint.y < -rect.height
            || destPoint.y > target.height) {
            return;
        }
        // target image has MSAA
        var msaa = this.context.glVersion === 2 && target.antialiasQuality > 0;
        if (mergeAlpha || msaa || blend) {
            if (!this._copyPixelFilter) {
                this._copyPixelFilter = new CopyPixelFilter();
            }
            this._copyPixelFilter.blend = blend;
            // because we in MSAA mode, but not has mergeAlpha - kill blending;
            this._copyPixelFilter.requireBlend = mergeAlpha;
            this.renderFilter(source, target, rect, destPoint, this._copyPixelFilter);
            this._copyPixelFilter.requireBlend = true;
        }
        else {
            rect = rect.clone();
            destPoint = destPoint.clone();
            if (destPoint.x < 0) {
                rect.x -= destPoint.x;
                rect.width += destPoint.x;
                destPoint.x = 0;
            }
            if (destPoint.y < 0) {
                rect.y -= destPoint.y;
                rect.height += destPoint.y;
                destPoint.y = 0;
            }
            if (rect.width > target.width - destPoint.x)
                rect.width = target.width - destPoint.x;
            if (rect.height > target.height - destPoint.y)
                rect.height = target.height - destPoint.y;
            this._stage.setRenderTarget(source, false);
            this._stage.setScissor(null);
            // TS !== AS3, it use a auto-type inference, not needed to insert it in all places
            var targetImageAbst = target.getAbstraction(this._stage);
            this.context.copyToTexture(targetImageAbst.getTexture(), rect, destPoint);
        }
    };
    FilterManager.prototype.threshold = function (source, target, rect, destPoint, operation, threshold, color, mask, copySource) {
        //early out for values that won't produce any visual update
        if (destPoint.x < -rect.width
            || destPoint.x > target.width
            || destPoint.y < -rect.height
            || destPoint.y > target.height) {
            return;
        }
        if (!this._thresholdFilter)
            this._thresholdFilter = new ThresholdFilter();
        this._thresholdFilter.operation = operation;
        this._thresholdFilter.threshold = threshold;
        this._thresholdFilter.color = color;
        this._thresholdFilter.mask = mask;
        this._thresholdFilter.copySource = copySource;
        this.renderFilter(source, target, rect, destPoint, this._thresholdFilter);
    };
    FilterManager.prototype.colorTransform = function (source, target, rect, colorTransform) {
        if (!this._copyPixelFilter) {
            this._copyPixelFilter = new CopyPixelFilter();
        }
        this._copyPixelFilter.blend = '';
        this._copyPixelFilter.requireBlend = false;
        this._copyPixelFilter.colorTransform = colorTransform;
        this.renderFilter(source, target, rect, rect, this._copyPixelFilter);
        this._copyPixelFilter.colorTransform = null;
    };
    FilterManager.MAX_TMP_TEXTURE = 4096;
    FilterManager.MIN_TMP_TEXTURE = 64;
    return FilterManager;
}());
export { FilterManager };
